import { ElementCommonPropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/element-common-property-converter';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import {
    DeltaOf,
    ITableData,
    ITableProps,
    ICTTableCell,
    ICTTableProperties,
} from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { TableInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/table-info/table-info-converter';
import { TableCellPropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/table-cell-property-converter';
import _set from 'lodash/set';
import { PlaceholderConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/placeholder/placeholder-converter';
import { FillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/fill-converter';
import {
    PictureInfoConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/picture-info/picture-info-converter';

export class TablePropertyConverter {
    /**
     * 表格 sdk -> ui 属性映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ITableProps>>,
        uiData: ITableModel,
    ): void {
        // **** 公共属性 ****
        if (sdkData.graphic?.graphicData?.tbl?.tblPr) {
            this.convertTablePropsFromSdkToUi(sdkData.graphic.graphicData.tbl.tblPr, uiData);
        }

        // 占位符信息转换
        if (sdkData.nvPr?.ph) {
            PlaceholderConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.ph, uiData);
        }
        // frame
        ElementCommonPropertyConverter.convertPartialDataFromSdkToUi(sdkData, uiData);

        // **** 独有属性 ****

        // 列宽信息、行高信息
        TableInfoConverter.convertPartialDataFromSdkToUi(sdkData, uiData);
        // 子节点
        // ui-sdk的子节点数据转换不在此处调用，而是在table-Model中
    }

    /**
     * 表格 ui -> sdk 属性映射
     * @param uiData
     * @param sdkData
     * @param rawUiData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<ITableModel>,
        sdkData: DeltaOf<ITableData>,
        rawUiData: ITableModel,
    ): void {
        // **** 公共属性 ****
        this.convertTablePropsFromUiToSdk(uiData, sdkData);

        // 占位符信息转换
        const placeholderInfo = uiData.props?.style?.placeholder || uiData.placeHolder;
        if (placeholderInfo) {
            sdkData.nvPr = sdkData.nvPr || {};
            sdkData.nvPr.ph = sdkData.nvPr.ph || {};
            PlaceholderConverter.convertPartialDataFromUiToSdk(placeholderInfo, sdkData.nvPr.ph);
        }
        // frame
        ElementCommonPropertyConverter.convertPartialDataFromUiToSdk(uiData, sdkData, rawUiData);

        // **** 独有属性 ****

        // 列宽信息、行高信息
        TableInfoConverter.convertPartialDataFromUiToSdk(uiData, sdkData);
        // 子节点
        if (uiData?.props?.items?.length) {
            // 组合出行信息
            const trArr: ({ tc: (ICTTableCell | undefined)[] } | undefined)[] = [];
            uiData.props.items.forEach((tr) => {
                const tcArr: (ICTTableCell | undefined)[] = [];
                tr?.forEach((cell) => {
                    if (Object.keys(cell || {}).length) {
                        const tc: ICTTableCell = {};
                        TableCellPropertyConverter.convertPartialDataFromUiToSdk(cell, tc);
                        tcArr.push(tc);
                    } else {
                        tcArr.push(undefined);
                    }
                });
                if (tcArr.length) {
                    trArr.push({ tc: tcArr });
                } else {
                    trArr.push(undefined);
                }
            });

            // 如果已存在行信息，则merge
            const existTrArr = sdkData?.graphic?.graphicData?.tbl?.tr;
            if (existTrArr?.length) {
                trArr.forEach((tr, index) => {
                    trArr[index] = Object.assign({}, tr, existTrArr[index]);
                });
            }
            _set(sdkData, 'graphic.graphicData.tbl.tr', trArr);
        }
    }

    public static convertTablePropsFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTableProperties>>,
        uiData: ITableModel,
    ) {
        const { firstCol, lastCol, firstRow, lastRow, bandCol, bandRow, tableStyleId } = sdkData;
        uiData.firstCol = !!firstCol;
        uiData.lastCol = !!lastCol;
        uiData.firstRow = !!firstRow;
        uiData.lastRow = !!lastRow;
        uiData.bandCol = !!bandCol;
        uiData.bandRow = !!bandRow;
        uiData.tableStyleId = tableStyleId;

        uiData.props.style.fillBackground = undefined;
        if (sdkData.solidFill || sdkData.gradFill) {
            // 对齐以前逻辑，每次都重新覆盖一个新对象
            uiData.props.style.fillBackground = {};
            FillConverter.convertPartialDataFromSdkToUi(sdkData, uiData.props.style.fillBackground);
        }
    }

    public static convertTablePropsFromUiToSdk(
        uiData: DeepPartial<ITableModel>,
        sdkData: DeltaOf<ITableData>,
    ) {
        const { firstCol, lastCol, firstRow, lastRow, bandCol, bandRow } = uiData;
        const tblPr = sdkData.graphic?.graphicData?.tbl?.tblPr || {};
        _set(sdkData, 'graphic.graphicData.tbl.tblPr', {
            ...tblPr,
            firstCol,
            lastCol,
            firstRow,
            lastRow,
            bandCol,
            bandRow,
        });
    }
}

export const VER2_3_TABLE_ATTR_PATH = {
    tblRowList: 'graphic.graphicData.tbl.tr',
    tblColList: 'graphic.graphicData.tbl.tblGrid.gridCol',
    tcList: 'tc',
    textBody: 'txBody',
    paragraphList: 'p',
    paragraphItem: 'egTextRun',
};
